// Canonical Spot the Pattern puzzle schema.
// Shared source of truth for the app quiz, TikTok/YouTube renders, and future surfaces.
// Additive: the in-app tap-phrase/type-answer mechanic still uses its own POOL fields.

export type SpotPatternPuzzle = {
  id: string;
  /** V1: single-element array (the existing message). Multi-clue progressive reveal is a later redesign. */
  clues: string[];
  /** 3 options: correct answer + 2 plausible distractors */
  choices: string[];
  /** must match one entry in choices */
  answer: string;
  explanation: string;
  difficulty: "easy" | "medium" | "hard";
};

export const SPOT_PATTERN_PUZZLES: SpotPatternPuzzle[] = [
  {
    id: "darvo-1",
    clues: ["I'm sorry you feel that way, but you always do this when I'm trying to explain myself."],
    choices: ["DARVO", "Guilt tripping", "Gaslighting"],
    answer: "DARVO",
    explanation:
      "The apology gets flipped into an attack on you before your original point is even addressed — deny, attack, reverse who's the victim.",
    difficulty: "easy",
  },
  {
    id: "character-attack-1",
    clues: ["You're overreacting, like you always do."],
    choices: ["Character attack", "Gaslighting", "Minimization"],
    answer: "Character attack",
    explanation:
      "Not a response to what happened — a label on who you are. 'Like you always do' turns one moment into a permanent flaw.",
    difficulty: "easy",
  },
  {
    id: "blame-shifting-1",
    clues: ["I only yelled because you pushed me to it."],
    choices: ["Blame shifting", "DARVO", "Guilt tripping"],
    answer: "Blame shifting",
    explanation: "Their behavior gets recast as your fault before they ever own it.",
    difficulty: "easy",
  },
  {
    id: "gaslighting-1",
    clues: ["Nobody else has ever complained about this. You're the only one who has a problem with it."],
    choices: ["Gaslighting", "Triangulation", "Minimization"],
    answer: "Gaslighting",
    explanation:
      "Your read on the situation gets replaced with 'everyone else disagrees with you' — isolating your perception, not addressing it.",
    difficulty: "medium",
  },
  {
    id: "isolation-1",
    clues: ["I just don't think you should hang out with them anymore. I only want what's best for you."],
    choices: ["Isolation", "Love bombing", "Guilt tripping"],
    answer: "Isolation",
    explanation: "Framed as care, but the actual ask is narrowing who else you can talk to.",
    difficulty: "medium",
  },
  {
    id: "triangulation-1",
    clues: ["Even my mom says you're too sensitive about this stuff."],
    choices: ["Triangulation", "Gaslighting", "Character attack"],
    answer: "Triangulation",
    explanation:
      "A third person gets pulled in to outvote you, so it's no longer just their opinion against yours.",
    difficulty: "medium",
  },
  {
    id: "guilt-leveraging-1",
    clues: ["I can't believe you'd bring that up now, after everything I've done for you."],
    choices: ["Guilt leveraging", "Guilt tripping", "DARVO"],
    answer: "Guilt leveraging",
    explanation: "Past favors get cashed in as a debt you're not allowed to question them while owing.",
    difficulty: "medium",
  },
  {
    id: "gaslighting-2",
    clues: ["You're remembering it wrong. That's not what happened at all."],
    choices: ["Gaslighting", "DARVO", "Minimization"],
    answer: "Gaslighting",
    explanation: "Not a disagreement about opinion — a direct rewrite of what you experienced.",
    difficulty: "medium",
  },
  {
    id: "guilt-tripping-1",
    clues: ["If you really loved me you'd stop hanging out with your friends so much."],
    choices: ["Guilt tripping", "Isolation", "Love bombing"],
    answer: "Guilt tripping",
    explanation:
      "Love gets made conditional on compliance — the ask is really about your friends, love is just the lever.",
    difficulty: "medium",
  },
  {
    id: "minimization-1",
    clues: ["Relax, I'm just teasing you. God, you can't take a joke anymore."],
    choices: ["Minimization", "Gaslighting", "Character attack"],
    answer: "Minimization",
    explanation:
      "Your reaction gets treated as the problem so the actual comment never has to be addressed.",
    difficulty: "medium",
  },
  {
    id: "stonewalling-1",
    clues: ["Fine. Whatever you want. I don't even care anymore."],
    choices: ["Stonewalling", "Minimization", "Guilt tripping"],
    answer: "Stonewalling",
    explanation: "Withdrawal used as a response — the conversation gets shut down instead of continued.",
    difficulty: "hard",
  },
  {
    id: "love-bombing-1",
    clues: [
      "I've never felt this close to anyone before. You're different from everyone else, I already know you're it for me.",
    ],
    choices: ["Love bombing", "Future faking", "Isolation"],
    answer: "Love bombing",
    explanation:
      "Intensity this early is doing a job — it's building trust faster than the relationship has actually earned.",
    difficulty: "hard",
  },
  {
    id: "projection-1",
    clues: ["You're the one who can't handle criticism. That's always been your problem."],
    choices: ["Projection", "Character attack", "DARVO"],
    answer: "Projection",
    explanation:
      "The trait they're accusing you of is the one they're actually avoiding looking at in themselves.",
    difficulty: "hard",
  },
  {
    id: "future-faking-1",
    clues: [
      "I know I messed up. I'm going to change, I promise, you'll see, things are going to be so different now.",
    ],
    choices: ["Future faking", "Love bombing", "Guilt leveraging"],
    answer: "Future faking",
    explanation: "A promise about tomorrow used to avoid dealing with what happened today.",
    difficulty: "hard",
  },
  {
    id: "covert-threat-1",
    clues: ["I was just joking, but if you're going to make it weird, maybe I can't be myself around you."],
    choices: ["Covert threat", "Minimization", "Guilt tripping"],
    answer: "Covert threat",
    explanation:
      "The real message is conditional — be careful how you respond, or something between you changes.",
    difficulty: "hard",
  },
];

export const SPOT_PATTERN_PUZZLES_BY_ID: Record<string, SpotPatternPuzzle> = Object.fromEntries(
  SPOT_PATTERN_PUZZLES.map((p) => [p.id, p]),
);

/**
 * Pattern name → deep-dive page URL. Keys must match the puzzle `answer`
 * (display name) exactly. Maintained one line at a time as Pattern Guide
 * pages ship — nothing else needs to change when a new page goes live.
 * Patterns without an entry render as plain text on the quiz result screen.
 */
export const PATTERN_DEEP_DIVE_URLS: Record<string, string> = {
  "DARVO": "/analysis/darvo-in-text-messages",
};
